import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Shield, 
  Key,
  Save,
  Loader2
} from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "" });
  
  // MFA state
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPending, setMfaPending] = useState(false);
  const [demoCode, setDemoCode] = useState("");
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setFormData({ name: response.data.name || "" });
    } catch (error) {
      toast.error("Failed to load profile");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile`, formData);
      setUser(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMFA = async () => {
    if (user.mfa_enabled) {
      // Disable MFA
      try {
        await axios.post(`${API}/profile/mfa/toggle`, { enable: false });
        setUser({ ...user, mfa_enabled: false });
        toast.success("MFA disabled");
      } catch (error) {
        toast.error("Failed to disable MFA");
      }
    } else {
      // Start MFA setup
      try {
        const response = await axios.post(`${API}/profile/mfa/toggle`, { enable: true });
        setDemoCode(response.data.code_for_demo || "");
        setMfaPending(true);
        setMfaDialogOpen(true);
        toast.info("Verification code sent to your email");
      } catch (error) {
        toast.error("Failed to start MFA setup");
      }
    }
  };

  const handleVerifyMFA = async () => {
    try {
      await axios.post(`${API}/profile/mfa/verify`, { code: mfaCode });
      setUser({ ...user, mfa_enabled: true });
      setMfaDialogOpen(false);
      setMfaCode("");
      setMfaPending(false);
      setDemoCode("");
      toast.success("MFA enabled successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const formData = new FormData();
      formData.append("current_password", currentPassword);
      formData.append("new_password", newPassword);
      
      await axios.post(`${API}/profile/change-password`, formData);
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-gray-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Profile Settings</h1>
            <p className="text-gray-400">Manage your account settings</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Profile Info Card */}
          <Card className="bg-[#12121a] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-[#10B981]" />
                Profile Information
              </CardTitle>
              <CardDescription className="text-gray-400">
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-white">{user?.email}</span>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-400">Display Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                  data-testid="name-input"
                />
              </div>
              
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="bg-[#10B981] hover:bg-[#059669]"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Security Card */}
          <Card className="bg-[#12121a] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#10B981]" />
                Security
              </CardTitle>
              <CardDescription className="text-gray-400">
                Manage your security settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* MFA Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <h3 className="font-medium text-white">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-400">
                    Add an extra layer of security with email verification codes
                  </p>
                </div>
                <Switch
                  checked={user?.mfa_enabled}
                  onCheckedChange={handleToggleMFA}
                  data-testid="mfa-toggle"
                />
              </div>
              
              {/* Change Password */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <h3 className="font-medium text-white">Password</h3>
                  <p className="text-sm text-gray-400">
                    Change your account password
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-white/10 text-gray-400 hover:text-white"
                  onClick={() => setPasswordDialogOpen(true)}
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Change
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Info Card */}
          <Card className="bg-[#12121a] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-400">
                <p>Account ID: <span className="text-white font-mono">{user?.id}</span></p>
                <p className="mt-2">
                  Member since: <span className="text-white">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* MFA Setup Dialog */}
        <Dialog open={mfaDialogOpen} onOpenChange={setMfaDialogOpen}>
          <DialogContent className="bg-[#12121a] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Enable Two-Factor Authentication</DialogTitle>
              <DialogDescription className="text-gray-400">
                Enter the 6-digit verification code sent to your email
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {demoCode && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-amber-500 text-sm">
                    Demo Mode: Your code is <span className="font-mono font-bold">{demoCode}</span>
                  </p>
                </div>
              )}
              <Label className="text-gray-400">Verification Code</Label>
              <Input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="mt-1 bg-[#1a1a24] border-white/10 text-white text-center text-2xl tracking-widest"
                data-testid="mfa-code-input"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setMfaDialogOpen(false);
                  setMfaCode("");
                  setMfaPending(false);
                  setDemoCode("");
                }}
                className="border-white/10 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifyMFA}
                disabled={mfaCode.length !== 6}
                className="bg-[#10B981] hover:bg-[#059669]"
              >
                Verify & Enable
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="bg-[#12121a] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Change Password</DialogTitle>
              <DialogDescription className="text-gray-400">
                Enter your current password and choose a new one
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-400">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label className="text-gray-400">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label className="text-gray-400">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 bg-[#1a1a24] border-white/10 text-white"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="border-white/10 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="bg-[#10B981] hover:bg-[#059669]"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
